import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
  getCountFromServer,
  orderBy,
  QueryConstraint,
  QueryFieldFilterConstraint
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { useStudentAuth } from '../StudentAuthContext';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';
import { startOfDay, endOfDay } from 'date-fns';
import './SideNavbar.css';

// redux
import { useAppSelector, useAppDispatch } from '../redux/store';
import { toggleSidebar } from '../redux/sidebarSlice';
import getCurrentScreen from '../Helper/GetScreen';

interface Task {
  id: string;
  task: string;
  userId: string;
  leadId: string;
  createdAt: Timestamp;
  dueDate: Timestamp;
  isDone: boolean;
  assignedBy?: string;
}

interface NavItem {
  name: string;
  normalIcon: string;
  activeIcon: string;
  link: string;
  isActive: boolean;
  order?: number;
  notificationCount?: { today: number; incoming: number } | undefined;
  activeRoutes?: string[];
  second_icon?: string;
}

// Memoized NotificationBadge component to prevent unnecessary re-renders
const NotificationBadge = React.memo(({ count, position, color }: { count: string, position?: string, color: string }) => (
  <div className={`${position} min-w-[20px] h-5 flex items-center justify-center ${color} text-white text-xs font-medium rounded-full px-1.5 z-10`}>
    {count}
  </div>
));

const SideNavbar: React.FC = () => {
  const location = useLocation();
  const { isAdmin, isManager, isStaff, currentUser, userData, departmentData } = useAuth();
  const { currentStudent } = useStudentAuth();
  const { currentSubAgent } = useSubAgentAuth();

  // Use refs to prevent unnecessary re-renders and maintain state across navigations
  const [todayFollowUpsCount, setTodayFollowUpsCount] = useState<number>(0);
  const [overdueFollowUpsCount, setOverdueFollowUpsCount] = useState<number>(0);
  const [todayTasksCount, setTodayTasksCount] = useState<number>(0);
  const [overdueTasksCount, setOverdueTasksCount] = useState<number>(0);
  const [leftSidePanelItems, setLeftSidePanelItems] = useState<any[]>([]);
  const [userLeftSidePanelItems, setUserLeftSidePanelItems] = useState<any[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const isCollapsed = useAppSelector((state) => state.sidebar.isCollapsed);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Use ref to prevent re-fetching on every render
  const lastFetchTime = useRef<number>(0);
  const FETCH_INTERVAL = 5 * 60 * 1000;

  const currentScreenFromRedux = useAppSelector(state => state.screen.currentScreen);
  const currentScreen = useMemo(() => {
    return getCurrentScreen(currentScreenFromRedux, isAdmin, isManager, isStaff);
  }, [isAdmin, isManager, isStaff, useAppSelector(state => state.screen.currentScreen)]);


  // Memoize user identification to prevent unnecessary calculations
  const userType = useMemo(() => {
    if (currentSubAgent) return 'subAgent';
    if (currentStudent) return 'student';
    return currentScreen;
  }, [currentSubAgent, currentStudent, currentScreen]);

  // Save and restore scroll position
  useEffect(() => {
    const scrollPosition = sessionStorage.getItem('sidebarScrollPosition');
    if (scrollPosition && sidebarRef.current) {
      sidebarRef.current.scrollTop = parseInt(scrollPosition, 10);
    }
  }, []);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    sessionStorage.setItem('sidebarScrollPosition', e.currentTarget.scrollTop.toString());
  }, []);

  // Memoized fetch functions to prevent recreation on every render
  const fetchFollowUpsCount = useCallback(async () => {
    if (!currentUser || !userData?.companyId) {
      console.log('No user or company ID');
      return;
    }

    try {
      console.log('Fetching follow-ups count...');
      const followUpsRef = collection(db, `companies/${userData.companyId}/followUps`);
      const now = new Date();
      const todayStart = startOfDay(now);
      const todayEnd = endOfDay(now);

      // Base query conditions - matching FollowUps.tsx logic exactly
      let todayQueryConditions: QueryConstraint[] = [
        where('isDone', '==', false),
        where('followUpTime', '>=', Timestamp.fromDate(todayStart)),
        where('followUpTime', '<=', Timestamp.fromDate(todayEnd))
      ];

      let overdueQueryConditions: QueryConstraint[] = [
        where('isDone', '==', false),
        where('followUpTime', '<', Timestamp.fromDate(todayStart))
      ];

      // Add role-based filters
      if (isManager && currentScreen === "Manager") {
        const usersQuery = query(
          collection(db, 'users'),
          where('companyId', '==', userData.companyId),
          where("managerIds", "array-contains", currentUser?.uid)
        );
        const usersSnapshot = await getDocs(usersQuery);
        const userIds = usersSnapshot.docs.map(doc => doc.id);

        if (userIds.length > 0) {
          todayQueryConditions.push(where('userId', 'in', userIds));
          overdueQueryConditions.push(where('userId', 'in', userIds));
        } else {
          // If no users under manager, set counts to 0
          setTodayFollowUpsCount(0);
          setOverdueFollowUpsCount(0);
          return;
        }
      } else if (isStaff && currentScreen === "Staff") {
        todayQueryConditions.push(where('userId', '==', currentUser.uid));
        overdueQueryConditions.push(where('userId', '==', currentUser.uid));
      }

      // Debugging logs
      console.log('Today query conditions:', todayQueryConditions);
      console.log('Overdue query conditions:', overdueQueryConditions);

      const [todaySnapshot, overdueSnapshot] = await Promise.all([
        getCountFromServer(query(followUpsRef, ...todayQueryConditions)),
        getCountFromServer(query(followUpsRef, ...overdueQueryConditions))
      ]);

      const todayCount = todaySnapshot.data().count;
      const overdueCount = overdueSnapshot.data().count;

      console.log(`Follow-ups count - Today: ${todayCount}, Overdue: ${overdueCount}`);

      setTodayFollowUpsCount(todayCount);
      setOverdueFollowUpsCount(overdueCount);
    } catch (error) {
      console.error('Error fetching follow-ups count:', error);
      // Set counts to 0 on error to avoid UI issues
      setTodayFollowUpsCount(0);
      setOverdueFollowUpsCount(0);
    }
  }, [currentUser, userData, isAdmin, isManager, isStaff, currentScreen]);

  // Updated fetchTasksCount function to match Tasks page logic exactly
  const fetchTasksCount = useCallback(async () => {
    if (!currentUser || !userData?.companyId) {
      console.log('No user or company ID');
      return;
    }

    try {
      console.log('Fetching tasks count...');
      const tasksRef = collection(db, `companies/${userData.companyId}/tasks`);

      // Fetch my tasks (assigned to me) - matching Tasks.tsx logic
      let myTasksQuery = query(
        tasksRef,
        where('userId', '==', currentUser.uid),
        where('isDone', '==', false)
      );

      // Fetch team tasks (assigned by me to others) - matching Tasks.tsx logic
      let teamTasksQuery = query(tasksRef);

      if (isAdmin && currentScreen === "Admin") {
        teamTasksQuery = query(
          teamTasksQuery,
          where('isDone', '==', false)
        );
      } else if (isManager && currentScreen === "Manager") {
        const usersQuery = query(
          collection(db, 'users'),
          where('isActive', '==', true),
          where('companyId', '==', userData.companyId),
          where("managerIds", "array-contains", currentUser?.uid)
        );
        const usersSnapshot = await getDocs(usersQuery);
        const userIds = usersSnapshot.docs.map(doc => doc.id);

        teamTasksQuery = query(
          teamTasksQuery,
          where('assignedBy', 'in', userIds),
          where('isDone', '==', false)
        );
      } else if (isStaff && currentScreen === "Staff") {
        teamTasksQuery = query(
          teamTasksQuery,
          where('assignedBy', '==', currentUser.uid),
          where('isDone', '==', false)
        );
      }

      const [myTasksSnapshot, teamTasksSnapshot] = await Promise.all([
        getDocs(myTasksQuery),
        getDocs(teamTasksQuery)
      ]);

      const fetchedMyTasks = myTasksSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as Task[];

      const fetchedTeamTasksAll = teamTasksSnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
      })) as Task[];

      const fetchedTeamTasks = fetchedTeamTasksAll.filter(task => task.userId !== currentUser.uid);

      // Combine my tasks and team tasks - matching Tasks.tsx display logic
      const allTasks = [...fetchedMyTasks, ...fetchedTeamTasks];

      // Use the same categorization logic as Tasks.tsx
      const categorizeTasksByDueDate = (tasks: Task[]) => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        return {
          dueToday: tasks.filter((task: Task) => {
            const dueDate = task.dueDate.toDate();
            return dueDate >= today && dueDate < tomorrow;
          }),
          overdue: tasks.filter((task: Task) => {
            const dueDate = task.dueDate.toDate();
            return dueDate < today;
          })
        };
      };

      const categorizedTasks = categorizeTasksByDueDate(allTasks);
      const todayCount = categorizedTasks.dueToday.length;
      const overdueCount = categorizedTasks.overdue.length;

      console.log(`Tasks count - Today: ${todayCount}, Overdue: ${overdueCount}`);

      setTodayTasksCount(todayCount);
      setOverdueTasksCount(overdueCount);
    } catch (error) {
      console.error('Error fetching tasks count:', error);
      // Set counts to 0 on error to avoid UI issues
      setTodayTasksCount(0);
      setOverdueTasksCount(0);
    }
  }, [currentUser, userData, isAdmin, isManager, isStaff, currentScreen]);

  const fetchLeftSidePanelItems = useCallback(async () => {
    if (!currentUser) return;

    try {
      const leftSidePanelItemsRef = collection(db, 'leftSidePanelItems');
      // Create a query with orderBy
      const q = query(leftSidePanelItemsRef, orderBy('order'));
      const leftSidePanelItemsSnapshot = await getDocs(q);

      const panelItems = leftSidePanelItemsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        order: doc.data().order ?? Number.MAX_SAFE_INTEGER
      })) as any[];

      // Debug: Log notification counts from database
      panelItems.forEach(item => {
        if (item.notificationCount) {
          console.log(`Database notification count for ${item.name}:`, item.notificationCount);
        }
      });

      // Filter active items and ensure they stay in order
      setLeftSidePanelItems(
        panelItems
          .filter((item: any) => item.isActive !== false)
          .sort((a: any, b: any) => {
            // Primary sort by order field
            const orderDiff = (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER);
            if (orderDiff !== 0) return orderDiff;

            // Secondary sort by name if order is the same
            return (a.name ?? '').localeCompare(b.name ?? '');
          })
      );
    } catch (error) {
      console.error('Error fetching left side panel items:', error);
    }
  }, [currentUser]);

  const fetchUserLeftSidePanelItems = useCallback(async () => {
    if (!currentUser) return;

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      const userData = userDocSnap.data();

      if (userData) {
        setUserLeftSidePanelItems(userData.leftSidePanelItems || []);
      }
    } catch (error) {
      console.error('Error fetching user left side panel items:', error);
    }
  }, [currentUser]);

  // Optimized data fetching with debouncing
  const fetchAllData = useCallback(async () => {
    const now = Date.now();
    if (now - lastFetchTime.current < FETCH_INTERVAL && isDataLoaded) {
      return;
    }

    lastFetchTime.current = now;

    try {
      await Promise.all([
        fetchFollowUpsCount(),
        fetchTasksCount(),
        fetchLeftSidePanelItems(),
        fetchUserLeftSidePanelItems()
      ]);
      setIsDataLoaded(true);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [fetchFollowUpsCount, fetchTasksCount, fetchLeftSidePanelItems, fetchUserLeftSidePanelItems, isDataLoaded]);

  // Use single effect with proper dependencies
  useEffect(() => {
    if (currentUser && userData?.companyId) {
      fetchAllData();
    }
  }, [currentUser, userData?.companyId, userType, fetchAllData]);

  useEffect(() => {
    setIsDataLoaded(false);
  }, [currentScreen]);

  // Set up interval for periodic updates
  useEffect(() => {
    if (!isDataLoaded) return;

    const interval = setInterval(() => {
      fetchFollowUpsCount();
      fetchTasksCount();
    }, FETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [isDataLoaded, fetchFollowUpsCount, fetchTasksCount]);





  // Memoized navigation items to prevent recreation on every render
  // Memoized navigation items to prevent recreation on every render
  const getNavItems = useMemo((): NavItem[] => {
    const matchingItems = leftSidePanelItems.filter(leftItem =>
      userLeftSidePanelItems.some(userItem => userItem === leftItem.id)
    );

    if (matchingItems.length > 0) {
      const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs', 'My Collaborations', 'Gmail Integration', 'Integrations'];
      const filteredItems = matchingItems.filter(item => {
        // For My Collaborations, only show for Staff users
        if (item.name === 'My Collaborations') {
          return currentScreen === 'Staff';
        }
        if (item.name === 'Gmail Integration') {
          return currentScreen === 'Staff' || currentScreen === 'Manager';
        }
        if (item.name === 'Integrations') {
          return currentScreen === 'Admin';
        }
        // For Staff users, hide all other restricted items
        if (restrictedItems.includes(item.name) && currentScreen === 'Staff') {
          return false;
        }
        // For Manager, hide restricted items except Tasks and Follow Ups
        if (restrictedItems.includes(item.name) && currentScreen === 'Manager' &&
          item.name !== 'Tasks' && item.name !== 'Follow Ups') {
          return false;
        }
        return true;
      });

      return filteredItems.map(item => {
        // Use database notification counts, but enhance with local state for Follow Ups and Tasks
        let notificationCount = item.notificationCount;

        // Override with local state calculations for Follow Ups and Tasks if they exist
        if (item.name === 'Follow Ups' && (todayFollowUpsCount > 0 || overdueFollowUpsCount > 0)) {
          notificationCount = { today: todayFollowUpsCount, incoming: overdueFollowUpsCount };
        } else if ((item.name === 'My Tasks' || item.name === 'Tasks') && (todayTasksCount > 0 || overdueTasksCount > 0)) {
          notificationCount = { today: todayTasksCount, incoming: overdueTasksCount };
        }

        // Fix dashboard links based on current screen
        let itemLink = item.link;
        if (item.name === 'Dashboard') {
          switch (currentScreen) {
            case 'Manager':
              itemLink = '/manager/dashboard';
              break;
            case 'Staff':
              itemLink = '/staff/dashboard';
              break;
            case 'Admin':
              itemLink = '/admin/dashboard';
              break;
            default:
              itemLink = item.link;
          }
        }

        // Debug log
        console.log(`Final notification count for ${item.name}:`, notificationCount);

        return {
          name: item.name || 'Unknown',
          normalIcon: `${process.env.PUBLIC_URL}${item.normalIcon}` || `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}${item.activeIcon}` || `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: itemLink,
          isActive: item.isActive !== false,
          notificationCount: notificationCount,
          activeRoutes: item.activeRoutes,
          second_icon: item.second_icon
        };
      });
    }

    if (currentScreen === 'Admin') {
      return [
        {
          name: 'Dashboard',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: '/admin/dashboard',
          isActive: true,
        },
        {
          name: 'Team Management',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-team.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-team-white.svg`,
          link: '/teammanagement',
          isActive: true,
        }
      ];
    } else if (currentScreen === 'Manager') {
      return [
        {
          name: 'Dashboard',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: '/manager/dashboard',
          isActive: true,
        }
      ]
    } else if (currentScreen === 'Staff') {
      return [
        {
          name: 'Dashboard',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: '/staff/dashboard',
          isActive: true,
        },
      ]
    }

    // Default navigation items based on user type
    const baseItems = {
      admin: [
        {
          name: 'Dashboard',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: '/admin/dashboard',
          isActive: true,
        },
        {
          name: 'Follow Ups',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-follow-up.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-follow-up-white.svg`,
          link: '/followups',
          isActive: true,
          notificationCount: { today: todayFollowUpsCount, incoming: overdueFollowUpsCount }
        },
        {
          name: 'My Tasks',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/hugeicons-task.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/hugeicons-task-white.svg`,
          link: '/staffstask',
          isActive: true,
          notificationCount: { today: todayTasksCount, incoming: overdueTasksCount }
        },
        {
          name: 'Team Management',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-team.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-team-white.svg`,
          link: '/teammanagement',
          isActive: true,
        },
        {
          name: 'Student Management',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/ActiveLead1.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/Staffmanage1.svg`,
          link: '/leadmanagement',
          isActive: true,
        },
        {
          name: 'Billing',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/invoice-outline.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/invoice-outline-white.svg`,
          link: '/invoices',
          isActive: true,
        },
        {
          name: 'Service',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service-white.svg`,
          link: '/service',
          isActive: true,
        },
        {
          name: 'Settings',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/settings-svgrepo.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/settings-svgrepo-white.svg`,
          link: '/settings',
          isActive: true,
        },
        {
          name: 'Reports',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/report-svgrepo-com.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/report-svgrepo-com-white.svg`,
          link: '/reports',
          isActive: true,
        },
        {
          name: 'Migration',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`,
          link: '/migration',
          isActive: true,
        },
        {
          name: 'Migration Beta',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`,
          link: '/migration-beta',
          isActive: true,
        },
        {
          name: 'Diverse Chat',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/diverse-chat.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/diverse-chat.svg`,
          second_icon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/new-tap-icon.svg`,
          link: 'https://diversechat.com',
          isActive: true,
        },
        {
          name: 'Integrations',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/integrations.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/integrations-white.svg`,
          link: '/integrations',
          isActive: true,
        },
        {
          name: 'Tools',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools-white.svg`,
          link: '/tools',
          isActive: true,
        },
        {
          name: 'Audit Logs',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/audit-logs.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/audit-logs-white.svg`,
          link: '/audit-logs',
          isActive: true,
        },
      ],
      manager: [
        {
          name: 'Dashboard',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: '/manager/dashboard',
          isActive: true,
        },
        {
          name: 'Student Management',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/ActiveLead1.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/Staffmanage1.svg`,
          link: '/leadmanagement',
          isActive: true,
        },
        {
          name: 'Follow Ups',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-follow-up.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-follow-up-white.svg`,
          link: '/followups',
          isActive: true,
          notificationCount: { today: todayFollowUpsCount, incoming: overdueFollowUpsCount }
        },
        {
          name: 'Tasks',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/hugeicons-task.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/hugeicons-task-white.svg`,
          link: '/staffstask',
          isActive: true,
          notificationCount: { today: todayTasksCount, incoming: overdueTasksCount }
        },
        {
          name: 'Service',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service-white.svg`,
          link: '/service',
          isActive: true,
        },
        {
          name: 'Gmail Integration',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/gmail.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/gmail-white.svg`,
          link: '/integrations/gmail',
          isActive: true,
        },
        {
          name: 'Reports',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/report-svgrepo-com.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/report-svgrepo-com-white.svg`,
          link: '/reports',
          isActive: true,
        },
        {
          name: 'Migration',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`,
          link: '/migration',
          isActive: true,
        },
        {
          name: 'Diverse Chat',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/diverse-chat.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/diverse-chat.svg`,
          second_icon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/new-tap-icon.svg`,
          link: 'https://diversechat.com',
          isActive: true,
        },
        {
          name: 'Tools',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools-white.svg`,
          link: '/tools',
          isActive: true,
        },
      ],
      staff: [
        {
          name: 'Dashboard',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: '/staff/dashboard',
          isActive: true,
        },
        {
          name: 'Student Management',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/ActiveLead1.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/Staffmanage1.svg`,
          link: '/leadmanagement',
          isActive: true,
        },
        {
          name: 'My Collaborations',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-my-collaborations.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-my-collaborations-white.svg`,
          link: '/collaborations',
          isActive: true,
        },
        {
          name: 'Follow Ups',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-follow-up.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-follow-up-white.svg`,
          link: '/followups',
          isActive: true,
          notificationCount: { today: todayFollowUpsCount, incoming: overdueFollowUpsCount }
        },
        {
          name: 'My Tasks',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/hugeicons-task.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/hugeicons-task-white.svg`,
          link: '/staffstask',
          isActive: true,
          notificationCount: { today: todayTasksCount, incoming: overdueTasksCount }
        },
        {
          name: 'Gmail Integration',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/gmail.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/gmail-white.svg`,
          link: '/integrations/gmail',
          isActive: true,
        },
        {
          name: 'Service',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service-white.svg`,
          link: '/service',
          isActive: true,
        },
        {
          name: 'Reports',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/report-svgrepo-com.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/report-svgrepo-com-white.svg`,
          link: '/reports',
          isActive: true,
        },
        {
          name: 'Migration',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/migration-white.svg`,
          link: '/migration',
          isActive: true,
        },
        {
          name: 'Diverse Chat',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/diverse-chat.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/diverse-chat.svg`,
          second_icon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/new-tap-icon.svg`,
          link: 'https://diversechat.com',
          isActive: true,
        },
        {
          name: 'Tools',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools-white.svg`,
          link: '/tools',
          isActive: true,
        },
      ],
      subAgent: [
        {
          name: 'Dashboard',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: '/subagent/dashboard',
          isActive: true,
        },
        {
          name: 'Student Management',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/ActiveLead1.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/Staffmanage1.svg`,
          link: '/subagent/leadmanagement',
          isActive: true,
        },
        {
          name: 'Tools',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/tools-white.svg`,
          link: '/subagent/tools',
          isActive: true,
        },
      ],
      student: [
        {
          name: 'Dashboard',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-dashboard-white.svg`,
          link: '/student-dashboard',
          isActive: true,
        },
        {
          name: 'My Profile',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/Myprofile.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/Myprofile-white.svg`,
          link: '/student-profile',
          isActive: true,
        },
        {
          name: 'Services',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-service-white.svg`,
          link: '/student-services',
          isActive: true,
        },
        {
          name: 'Document Uploaded',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/iconoir_profile-circle.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/iconoir_profile-circle-white.svg`,
          link: '/student-documents',
          isActive: true,
        },
        {
          name: 'Tickets',
          normalIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-tickets.svg`,
          activeIcon: `${process.env.PUBLIC_URL}/images/desktop/sidebaricon/icon-tickets-white.svg`,
          link: '/student-raise-tickets',
          isActive: true,
        },
      ]
    };

    // Return appropriate items based on user type and screen
    if (currentSubAgent) {
      return baseItems.subAgent;
    }

    if (currentStudent) {
      return baseItems.student;
    }

    // Return based on screen with proper filtering
    let items: NavItem[];
    switch (currentScreen) {
      // case 'Admin':
      //   items = baseItems.admin;
      //   break;
      // case 'Manager':
      //   items = baseItems.manager;
      //   break;
      // case 'Staff':
      //   items = baseItems.staff;
      //   break;
      default:
        return [];
    }

    // Filter restricted items for Staff and Manager
    const restrictedItems = ['Team Management', 'Settings', 'Migration Beta', 'Audit Logs'];
    return items.filter(item => {
      if (restrictedItems.includes(item.name) && (currentScreen === 'Staff' || currentScreen === 'Manager')) {
        return false;
      }
      return true;
    });
  }, [
    leftSidePanelItems,
    userLeftSidePanelItems,
    currentScreen,
    userType,
    todayFollowUpsCount,
    overdueFollowUpsCount,
    todayTasksCount,
    overdueTasksCount
  ]);





  const isCallCRMActive = useCallback((item: any) => {
    if (item.name === 'Call CRM' || item.name === 'Team Management') {
      return item.activeRoutes ? item.activeRoutes.includes(location.pathname) : false;
    }
    return false;
  }, [location.pathname]);

  // Memoized NavItem component to prevent unnecessary re-renders
  const NavItem = React.memo(({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.link || isCallCRMActive(item);

    if (!item.isActive) {
      return (
        <div className={`flex items-center transition-all duration-300 ${isCollapsed
          ? 'w-[50px] h-[50px] rounded-full mx-auto justify-center'
          : 'w-full h-[50px] px-4'
          } text-black relative cursor-not-allowed hover:bg-gray-100 ${!isCollapsed && 'hover:rounded-[32px]'}`}>
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
            <img
              src={item.normalIcon}
              alt={`${item.name} icon`}
              className="w-6 h-6 min-w-[24px] min-h-[24px] max-w-[24px] max-h-[24px] object-contain"
            />
            <span className={`font-[Montserrat] text-[16px] font-medium truncate ${isCollapsed ? 'hidden' : 'ml-3 block'}`}>
              {item.name}
            </span>
          </div>
        </div>
      );
    }

    // Check if it's an external link
    if (item.link.startsWith('http://') || item.link.startsWith('https://')) {
      return (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center ${isCollapsed ? 'justify-center' : ''} transition-all duration-300 ${isActive
            ? `${isCollapsed
              ? 'w-[50px] h-[50px] rounded-full mx-auto flex items-center justify-center'
              : 'w-full h-[50px] rounded-[32px] px-4'
            } bg-gradient-to-l from-[#A7C8E7] to-[#00A6D9] text-white`
            : `${isCollapsed ? 'w-[50px] h-[50px] justify-center' : 'w-full h-[50px] px-4'} text-black hover:bg-gray-100 hover:rounded-[32px]`
            }`}
        >
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'w-full'}`}>
            <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
              <img
                src={isActive ? item.activeIcon : item.normalIcon}
                alt={`${item.name} icon`}
                className="w-6 h-6 flex-shrink-0 object-contain"
              />

              <span className={`font-[Montserrat] text-[16px] font-medium truncate ${isCollapsed ? 'hidden' : 'ml-3 block'}`}>
                {item.name}
              </span>
              
              {item.second_icon && (
                <img
                  src={`${process.env.PUBLIC_URL}${item.second_icon}`}
                  alt={`${item.name} second icon`}
                  className="w-4 h-4 ml-1 flex-shrink-0 object-contain"
                />
              )}
            </div>

            {/* Notification badges */}
            {item.notificationCount && (
              <div className={`relative flex items-center ${isCollapsed ? 'ml-2' : 'ml-auto gap-1'}`}>
                {(() => {
                  console.log(`Rendering ${item.name} with notificationCount:`, item.notificationCount);
                  return null;
                })()}
                {item.notificationCount.today > 0 && (
                  <NotificationBadge
                    count={isCollapsed && item.notificationCount.today > 10 ? '10+' : `${item.notificationCount.today}`}
                    position={isCollapsed ? 'absolute -top-5 right-2' : ''}
                    color="bg-green-500"
                  />
                )}
                {item.notificationCount.incoming > 0 && (
                  <NotificationBadge
                    count={isCollapsed && item.notificationCount.incoming > 10 ? '10+' : `${item.notificationCount.incoming}`}
                    position={isCollapsed ? 'absolute -top-5 -right-5' : ''}
                    color="bg-red-500"
                  />
                )}
              </div>
            )}
          </div>
        </a>
      );
    }

    return (
      <NavLink
        to={item.link}
        className={`flex items-center ${isCollapsed ? 'justify-center' : ''} transition-all duration-300 ${isActive
          ? `${isCollapsed
            ? 'w-[50px] h-[50px] rounded-full mx-auto flex items-center justify-center'
            : 'w-full h-[50px] rounded-[32px] px-4'
          } bg-gradient-to-l from-[#A7C8E7] to-[#00A6D9] text-white`
          : `${isCollapsed ? 'w-[50px] h-[50px] justify-center' : 'w-full h-[50px] px-4'} text-black hover:bg-gray-100 hover:rounded-[32px]`
          }`}
      >
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'w-full'}`}>
          <div className={`flex items-center ${isCollapsed ? 'justify-center' : ''}`}>
            <img
              src={isActive ? item.activeIcon : item.normalIcon}
              alt={`${item.name} icon`}
              className="w-6 h-6 flex-shrink-0 object-contain"
            />
            {item.second_icon && (
              <img
                src={`${process.env.PUBLIC_URL}${item.second_icon}`}
                alt={`${item.name} second icon`}
                className="w-4 h-4 ml-1 flex-shrink-0 object-contain"
              />
            )}
            <span className={`font-[Montserrat] text-[16px] font-medium truncate ${isCollapsed ? 'hidden' : 'ml-3 block'}`}>
              {item.name}
            </span>
          </div>

          {/* Notification badges */}
          {item.notificationCount && (
            <div className={`relative flex items-center ${isCollapsed ? 'ml-2' : 'ml-auto gap-1'}`}>
              {(() => {
                console.log(`Rendering ${item.name} with notificationCount:`, item.notificationCount);
                return null;
              })()}
              {item.notificationCount.today > 0 && (
                <NotificationBadge
                  count={isCollapsed && item.notificationCount.today > 10 ? '10+' : `${item.notificationCount.today}`}
                  position={isCollapsed ? 'absolute -top-5 right-2' : ''}
                  color="bg-green-500"
                />
              )}
              {item.notificationCount.incoming > 0 && (
                <NotificationBadge
                  count={isCollapsed && item.notificationCount.incoming > 10 ? '10+' : `${item.notificationCount.incoming}`}
                  position={isCollapsed ? 'absolute -top-5 -right-5' : ''}
                  color="bg-red-500"
                />
              )}
            </div>
          )}
        </div>
      </NavLink>
    );
  });

  // Show loading state to prevent initial blink
  if (!isDataLoaded && currentUser && userData?.companyId) {
    return (
      <div className="fixed left-1" style={{ top: '140px' }}>
        <div className={`${isCollapsed ? 'w-[80px]' : 'w-[300px]'} bg-white rounded-[32px] shadow-lg transition-all duration-300 h-[calc(100vh-150px)] flex flex-col`}>
          <div className="w-full h-[40px] flex items-center justify-end p-4 border-b border-gray-100 rounded-t-[32px] bg-white">
            <button
              onClick={() => dispatch(toggleSidebar())}
              className="flex items-center justify-center"
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/${isCollapsed ? 'chevron-right' : 'chevron-left'}.svg`}
                alt="Toggle sidebar"
                className="w-4 h-4"
              />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-pulse text-gray-500">Loading...</div>
          </div>
          <div className="w-full h-[40px] flex items-center justify-end p-4 border-t border-gray-100 rounded-b-[32px] bg-white">
            <div className="w-4 h-4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed left-1" style={{ top: '140px' }}>
      <div className={`${isCollapsed ? 'w-[80px]' : 'w-[300px]'} bg-white rounded-[32px] shadow-lg transition-all duration-300 h-[calc(100vh-150px)] flex flex-col`}>
        <div className="w-full h-[40px] flex items-center justify-end p-4 border-b border-gray-100 rounded-t-[32px] bg-white">
          <button
            onClick={() => dispatch(toggleSidebar())}
            className="flex items-center justify-center"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/${isCollapsed ? 'chevron-right' : 'chevron-left'}.svg`}
              alt="Toggle sidebar"
              className="w-4 h-4"
            />
          </button>
        </div>

        <div
          ref={sidebarRef}
          onScroll={handleScroll}
          className="side-navbar flex-1 overflow-y-auto p-4 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        >
          <nav className="flex flex-col space-y-1">
            {getNavItems.map((item) => (
              <div
                key={item.name}
                className="relative group"
                title={
                  isCollapsed
                    ? item.name === 'Follow Ups'
                      ? "This section shows only today's upcoming follow-ups Count based on time."
                      : item.name
                    : item.name === 'Follow Ups'
                      ? "This section shows only today's upcoming follow-ups Count based on time."
                      : ''
                }
              >
                <NavItem item={item} />
              </div>
            ))}
          </nav>
        </div>

        <div className="w-full h-[40px] flex items-center justify-end p-4 border-t border-gray-100 rounded-b-[32px] bg-white">
          <div className="w-4 h-4"></div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(SideNavbar);